class SearchModal {
  static #DEBOUNCE_MS  = 350;
  static #SEARCH_LIMIT = 6;
  #modal       = null;
  #openBtn     = null;
  #closeBtn    = null;
  #input       = null;
  #clearBtn    = null;
  #errorEl     = null;
  #hintsEl     = null;
  #resultsEl   = null;
  #moreWrap    = null;
  #moreBtn     = null;
  #debounceTimer = null;
  #activeQuery   = '';

  constructor() {
    this.#modal = document.getElementById('searchModal');
    if (!this.#modal) return;

    this.#openBtn   = document.querySelector('.header-categories__search');
    this.#closeBtn  = this.#modal.querySelector('.search-modal__close');
    this.#input     = this.#modal.querySelector('#searchModalInput');
    this.#clearBtn  = this.#modal.querySelector('.search-modal__clear');
    this.#errorEl   = this.#modal.querySelector('#searchModalError');
    this.#hintsEl   = this.#modal.querySelector('#searchModalHints');
    this.#resultsEl = this.#modal.querySelector('#searchModalResults');
    this.#moreWrap  = this.#modal.querySelector('#searchModalMore');
    this.#moreBtn   = this.#modal.querySelector('#searchModalMoreBtn');

    this.#bindEvents();
  }

  #open() {
    this.#modal.classList.add('is-open');
    this.#modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('search-modal-open');
    setTimeout(() => this.#input.focus(), 50);
  }

  #close() {
    this.#modal.classList.remove('is-open');
    this.#modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('search-modal-open');
  }

  #bindEvents() {
    if (this.#openBtn) {
      this.#openBtn.addEventListener('click', () => this.#open());
    }

    this.#closeBtn.addEventListener('click', () => this.#close());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.#modal.classList.contains('is-open')) {
        this.#close();
      }
    });

    this.#modal.addEventListener('click', (e) => {
      if (e.target === this.#modal) this.#close();
    });

    if (this.#hintsEl) {
      this.#hintsEl.querySelectorAll('.search-modal__hint').forEach((btn) => {
        btn.addEventListener('click', () => {
          const word = btn.textContent.trim();
          this.#input.value = word;
          this.#clearBtn.hidden = false;
          this.#hideError();
          this.#hideHints();
          this.#triggerSearch(word);
        });
      });
    }

    this.#input.addEventListener('input', () => {
      const val = this.#input.value.trim();
      this.#clearBtn.hidden = !this.#input.value.length;
      this.#hideError();
      clearTimeout(this.#debounceTimer);

      if (!val) {
        this.#clearResults();
        this.#showHints();
        return;
      }

      this.#hideHints();
      this.#debounceTimer = setTimeout(
        () => this.#triggerSearch(val),
        SearchModal.#DEBOUNCE_MS
      );
    });

    this.#input.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();

      const val = this.#input.value.trim();
      if (!val) {
        this.#showError();
        return;
      }

      this.#hideError();
      clearTimeout(this.#debounceTimer);
      this.#triggerSearch(val);
    });

    this.#clearBtn.addEventListener('click', () => {
      this.#input.value = '';
      this.#clearBtn.hidden = true;
      this.#clearResults();
      this.#showHints();
      this.#hideError();
      this.#input.focus();
    });
  }

  async #triggerSearch(query) {
    if (query === this.#activeQuery) return;
    this.#activeQuery = query;

    try {
      const formData = new FormData();
      formData.append('action', 'search_posts');
      formData.append('nonce', window.backendData?.searchNonce ?? '');
      formData.append('query', query);
      formData.append('limit', String(SearchModal.#SEARCH_LIMIT));

      const response = await fetch(
        window.backendData?.ajaxUrl ?? '/wp-admin/admin-ajax.php',
        { method: 'POST', body: formData }
      );

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const json = await response.json();

      if (!json.success || !Array.isArray(json.data?.posts) || json.data.posts.length === 0) {
        this.#renderNothing();
        return;
      }

      const { posts, total, searchUrl } = json.data;
      this.#renderResults(posts, query);

      this.#moreWrap.hidden = total <= SearchModal.#SEARCH_LIMIT;
      if (total > SearchModal.#SEARCH_LIMIT) {
        this.#moreBtn.href = searchUrl;
      }
    } catch (err) {
      console.error('[SearchModal] Fetch error:', err);
      this.#renderNothing();
    }
  }

  #renderResults(posts, query) {
    this.#resultsEl.innerHTML = posts
      .map((post) => this.#buildPostItem(post, query))
      .join('');

    this.#bindResultHoverPreviews();
  }

  #bindResultHoverPreviews() {
    const items = this.#resultsEl.querySelectorAll('.popular-posts__item');

    items.forEach((item) => {
      const imageWrap = item.querySelector('.popular-posts__item-image');

      if (!imageWrap) return;

      item.addEventListener('mouseenter', () => {
        imageWrap.classList.add('popular-posts__item-image--visible');
      });

      item.addEventListener('mouseleave', () => {
        imageWrap.classList.remove('popular-posts__item-image--visible');
        imageWrap.style.transform = 'translate3d(-9999px, -9999px, 0)';
      });

      item.addEventListener('mousemove', (event) => {
        if (window.innerWidth <= 1280) return;

        const cursorOffsetX = 20;
        const cursorOffsetY = 0;
        let x = event.clientX + cursorOffsetX;
        let y = event.clientY + cursorOffsetY;

        const rect = imageWrap.getBoundingClientRect();
        const overflowRight = x + rect.width - window.innerWidth;

        if (overflowRight > 0) {
          x -= overflowRight + 20;
        }

        const overflowBottom = y + rect.height - window.innerHeight;
        if (overflowBottom > 0) {
          y -= overflowBottom + 20;
        }

        if (y < 0) y = 0;

        imageWrap.style.transform = `translate3d(${x}px, ${y}px, 0)`;
      });
    });
  }

  #buildPostItem(post, query) {
    const title   = this.#highlightText(this.#escapeHtml(post.title), query);
    const excerpt = post.excerpt
      ? `<p class="search-result__excerpt">${this.#highlightText(this.#escapeHtml(post.excerpt), query)}</p>`
      : '';

    const categoryHtml = post.category
      ? `<div class="popular-posts__category">${this.#escapeHtml(post.category)}</div>`
      : '';

    const imageHtml = post.thumbnail
      ? `<div class="popular-posts__item-image">
           <img class="popular-posts__image" src="${this.#escapeHtml(post.thumbnail)}" alt="${this.#escapeHtml(post.title)}" loading="lazy">
         </div>`
      : '';

    return `
      <a href="${this.#escapeHtml(post.url)}" class="popular-posts__item">
        <div class="popular-posts__item-content">
          ${categoryHtml}
          <h3 class="popular-posts__item-title">${title}</h3>
          ${excerpt}
        </div>
        ${imageHtml}
      </a>`;
  }

  #renderNothing() {
    this.#resultsEl.innerHTML = `
      <div class="search-modal__nothing">
        <h2 class="search-modal__nothing-title">Nothing found!</h2>
        <p class="search-modal__nothing-text">Apologies, but no results were found for the requested archive. Try using the search with a relevant phrase to find the post you are looking for.</p>
      </div>`;
    this.#moreWrap.hidden = true;
  }

  #clearResults() {
    this.#resultsEl.innerHTML = '';
    this.#moreWrap.hidden = true;
    this.#activeQuery = '';
  }

  #highlightText(escapedText, rawQuery) {
    if (!rawQuery) return escapedText;
    const escapedQuery = this.#escapeHtml(rawQuery).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return escapedText.replace(new RegExp(`(${escapedQuery})`, 'gi'), '<mark>$1</mark>');
  }

  #escapeHtml(str) {
    return String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  #showError() { this.#errorEl.hidden = false; }
  #hideError() { this.#errorEl.hidden = true; }
  #showHints() { if (this.#hintsEl) this.#hintsEl.hidden = false; }
  #hideHints() { if (this.#hintsEl) this.#hintsEl.hidden = true; }
}

document.addEventListener('DOMContentLoaded', () => new SearchModal());
